import _ from "lodash";
import { useEffect, useRef } from "react";
import { getKeyboardFocusableElements } from "Utils/AccessibilityHelpers";

/**
 * Custom hook that traps focus within a specified container when active.
 *
 * @param {Object} options - The options for the focus trap.
 * @param {boolean} options.isActive - Determines if the focus trap is active.
 * @param {boolean} [options.shouldDefaultFocusOnFirstChild=true] - Determines if the first focusable element should be focused by default.
 * @param {boolean} [options.shouldStopPropagation=true] - Determines if the event propagation should be stopped.
 * @param {number} [options.delayInMsForFirstFocus=0] - The delay in milliseconds before focusing the first element.
 * @param {boolean} [options.shouldFocusOnLastElementOnUnmount=true] - Determines whether focus should return to the last active element before the focus trap was activated when the component unmounts or when isActive is set to false.
 * @param {Object} [options.defaultFocusRef=null] - A ref object pointing to the element that should receive focus by default. If provided, this element will be focused instead of the first focusable element.
 * @returns {Object} - The ref object for the container element.
 */

const useFocusTrap = ({
  isActive,
  shouldDefaultFocusOnFirstChild = true,
  shouldStopPropagation = true,
  delayInMsForFirstFocus = 0,
  shouldFocusOnLastElementOnUnmount = true,
  defaultFocusRef = null,
}) => {
  const containerRef = useRef(null);
  const lastActiveElement = useRef(null);

  useEffect(() => {
    if (isActive) {
      lastActiveElement.current = document.activeElement;
    } else {
      lastActiveElement.current = null;
    }

    return () => {
      if (lastActiveElement.current && shouldFocusOnLastElementOnUnmount) {
        lastActiveElement.current.focus();
      }
    };
  }, [isActive, shouldFocusOnLastElementOnUnmount]);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;
    containerRef.current.setAttribute("tabindex", -1);

    const handleFocus = event => {
      const focusableElements = getFocusableElements(containerRef.current);

      const firstElement = _.first(focusableElements);
      const lastElement = _.last(focusableElements);

      if (_.isEmpty(focusableElements)) {
        event.preventDefault();
        containerRef?.current?.focus();
        return;
      }

      if (event.target === containerRef.current && event.shiftKey) {
        event.preventDefault();
        if (lastElement) {
          lastElement.focus();
        }
        return;
      }

      if (event.target === lastElement && !event.shiftKey) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      if (event.target === firstElement && event.shiftKey) {
        event.preventDefault();
        lastElement.focus();
        return;
      }
    };

    const trapFocus = event => {
      if (event.key === "Tab") {
        if (shouldStopPropagation) {
          event.stopPropagation();
        }
        handleFocus(event);
      }
    };

    const element = containerRef.current;
    element.addEventListener("keydown", trapFocus);

    // Focus the default element when the component mounts
    const focusableElements = getFocusableElements(containerRef.current);
    setTimeout(() => {
      // If a specific ref is provided, focus that element
      if (defaultFocusRef?.current) {
        defaultFocusRef.current.focus();
      } else if (
        !_.isEmpty(focusableElements) &&
        shouldDefaultFocusOnFirstChild
      ) {
        const firstElement = _.first(focusableElements);
        firstElement?.focus();
      } else {
        containerRef?.current?.focus();
      }
    }, delayInMsForFirstFocus);

    return () => {
      element.removeEventListener("keydown", trapFocus);
    };
  }, [
    defaultFocusRef,
    delayInMsForFirstFocus,
    isActive,
    shouldDefaultFocusOnFirstChild,
    shouldStopPropagation,
  ]);

  return containerRef;
};

function getFocusableElements(container) {
  const focusableElements = getKeyboardFocusableElements(container);
  const radioGroups = new Set();

  const filteredElements = _.filter(_.toArray(focusableElements), element => {
    const style = window.getComputedStyle(element);
    const isFocusable =
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      (element.contentEditable === "true" || element.tabIndex !== -1);

    if (
      element.tagName === "INPUT" &&
      element.getAttribute("type") === "radio"
    ) {
      if (element.checked && isFocusable && !radioGroups.has(element.name)) {
        radioGroups.add(element.name);
        return true;
      }

      return false;
    }

    return isFocusable;
  });

  return filteredElements;
}

export default useFocusTrap;
